import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Alert } from '@/components/ui/alert';
import { A4_PAGE_HEIGHT, A4_PAGE_WIDTH, DEFAULT_FONT } from '@/constants';
import { useAuth } from '@/context/AuthContext';
import { OptionalReportMeasurementProvider } from '@/context/ReportMeasurementContext';
import { useConfirmDelete } from '@/hooks/use-confirm-delete';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useReportSections } from '@/hooks/use-report-sections';
import { capture } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import { resolvePreviewUrl } from '@/utils/preview-cache';
import { validateImageFile } from '@/utils/validate-image-file';
import { attrToRealId, type InsertTarget } from './block-selection/blockClipboard';
import { useTestBlockSelection } from './block-selection/TestBlockSelectionContext';
import { HiddenReportMeasurer } from './HiddenReportMeasurer';
import { PageContextMenu } from './PageContextMenu';
import { stripSplitBlockId } from './pagination/utils';
import { textBlockFromHtml, instrumentBlocksFromGrid } from './paste/blockFromClipboard';
import { classifyPaste } from './paste/classifyPaste';
import { readSystemClipboard } from './paste/readSystemClipboard';
import { PatientInfoTable } from './PatientInfoTable';
import { setPendingImageFile } from './pending-image-files';
import PreviewPsychologistSignature from './PreviewPsychologistSignature';
import { ReportFooter } from './ReportFooter';
import { ReportPageFootnotes } from './ReportFootnotes';
import { ReportHeader } from './ReportHeader';
import { ReportPreviewSkeleton } from './ReportPreviewSkeleton';
import { ReportSection } from './ReportSection';
import { usePastePreview } from './usePastePreview';
import { useReportFootnotes } from './useReportFootnotes';
import { RenderableSection, useReportPagination } from './useReportPagination';
import { useReportPaste } from './useReportPaste';
import type { PasteRoute } from './paste/classifyPaste';
import type { EditingTextSection, Psychologist, ReportData, Section } from './types';
import type { TestBlock } from '@/types/report-payload';

type ReportPreviewProps = {
    data: ReportData;
    onUpdate: (updates: Partial<ReportData> | ((prev: ReportData) => Partial<ReportData>)) => void;
    onTotalPagesChange?: (totalPages: number) => void;
    disabled?: boolean;
    onAddTest: (blockId?: string, mode?: 'default' | 'inline') => void;
    onAddChartFromTable: (blockId: string) => void;
    onEditChart: (block: TestBlock) => void;
    /** Toolbox-style block creation, used by the page menu for charts (which
     *  route through the config modal rather than being created directly). */
    onAddBlock: (toolId: string) => void;
    /** Opens the instrument selector; `insert` overrides append-at-end so the
     *  page menu can drop the created blocks at the clicked position. */
    /**
     * `anchorRealId` is the same click target as `insert`, in the form the
     * free/pasted routes need: they build their own group and place it with
     * `insertInstrumentBlocks`, which takes an anchor rather than an inserter.
     */
    onAddInstrument?: (
        insert?: (blocks: TestBlock[]) => void,
        anchorRealId?: string | null,
    ) => void;
    /** True while a block drag is in flight — pagination holds its current
     *  layout so droppable ids stay stable under the drag. */
    freezePagination?: boolean;
    onSectionClick?: (sectionId: string) => void;
    onSectionPageMapChange?: (map: Record<string, number>) => void;
    showOnlyFirstPage?: boolean;
    isLoading?: boolean;
    commentCountsBySection?: Partial<Record<string, number>>;
    onOpenAllComments?: () => void;
};

type ReportPreviewContentProps = ReportPreviewProps & {
    editingSection: EditingTextSection;
    onEditingSectionChange: (section: EditingTextSection) => void;
    user: Psychologist | null;
    handleDeleteRequest: (sectionId: string) => void;
    editingBlockId: string | number | null;
    setEditingBlockId: (id: string | number | null) => void;
    sections: Section[];
};

const isCompletedStatus = (status?: string) => status?.toLowerCase() === 'completed';

const ReadOnlyBanner = () => (
    <Alert variant="info">
        <p className="font-medium">Laudo concluído!</p>
        <p className="mt-0.5 opacity-80">Este laudo está em modo somente leitura.</p>
    </Alert>
);

function ReportPreviewComponent({
    data,
    onUpdate,
    onTotalPagesChange,
    disabled = false,
    onAddTest,
    onAddChartFromTable,
    onEditChart,
    onAddBlock,
    onAddInstrument,
    onSectionClick,
    onSectionPageMapChange,
    showOnlyFirstPage,
    isLoading,
    commentCountsBySection,
    onOpenAllComments,
    freezePagination = false,
}: ReportPreviewProps) {
    const confirmDelete = useConfirmDelete();
    const { user } = useAuth();

    const effectiveDisabled = disabled || isCompletedStatus(data.status);

    const [editingBlockId, setEditingBlockId] = useState<string | number | null>(null);
    const [editingSection, setEditingSection] = useState<EditingTextSection>(null);

    // A block created from the tools panel opens with the caret already in it.
    // No timing guard is needed: `editingBlockId` is state, so the block picks
    // it up whenever pagination gets around to rendering it.
    const { pendingEditBlockId, setPendingEditBlockId } = useReportWizard();
    useEffect(() => {
        if (!pendingEditBlockId) return;

        setEditingBlockId(pendingEditBlockId);
        setPendingEditBlockId(null);
    }, [pendingEditBlockId, setPendingEditBlockId]);

    const handleConfirmDelete = useCallback(
        (sectionId: string) => {
            onUpdate((prevData) => {
                const currentSection = (prevData[sectionId as keyof ReportData] ?? {}) as {
                    content?: string;
                    section_title?: string | null;
                };

                return {
                    [sectionId]: {
                        ...currentSection,
                        content: '',
                        section_title: currentSection.section_title || null,
                    },
                };
            });
        },
        [onUpdate],
    );

    const handleDeleteRequest = useCallback(
        (sectionId: string) => {
            confirmDelete({
                title: 'Remover Texto?',
                description:
                    'Tem certeza que deseja remover este texto? Esta ação não pode ser desfeita.',
                confirmText: 'Sim, remover',
                onConfirm: (close) => {
                    handleConfirmDelete(sectionId);
                    close();
                },
            });
        },
        [handleConfirmDelete, confirmDelete],
    );

    const sections = useReportSections(data);

    return (
        <OptionalReportMeasurementProvider>
            <ReportPreviewContent
                data={data}
                onUpdate={onUpdate}
                onTotalPagesChange={onTotalPagesChange}
                disabled={effectiveDisabled}
                onAddTest={onAddTest}
                onAddChartFromTable={onAddChartFromTable}
                onEditChart={onEditChart}
                onAddBlock={onAddBlock}
                onAddInstrument={onAddInstrument}
                onSectionClick={onSectionClick}
                editingSection={editingSection}
                onEditingSectionChange={setEditingSection}
                onSectionPageMapChange={onSectionPageMapChange}
                user={user}
                commentCountsBySection={commentCountsBySection}
                onOpenAllComments={onOpenAllComments}
                handleDeleteRequest={handleDeleteRequest}
                editingBlockId={editingBlockId}
                setEditingBlockId={setEditingBlockId}
                sections={sections as Section[]}
                showOnlyFirstPage={showOnlyFirstPage}
                isLoading={isLoading}
                freezePagination={freezePagination}
            />
        </OptionalReportMeasurementProvider>
    );
}

const ReportPreviewContent = memo(function ReportPreviewContent({
    data,
    onUpdate,
    onTotalPagesChange,
    disabled,
    onAddTest,
    onAddChartFromTable,
    onEditChart,
    onAddBlock,
    onAddInstrument,
    onSectionClick,
    editingSection,
    onEditingSectionChange,
    onSectionPageMapChange,
    user,
    commentCountsBySection,
    onOpenAllComments,
    handleDeleteRequest,
    editingBlockId,
    setEditingBlockId,
    sections,
    showOnlyFirstPage,
    isLoading,
    freezePagination = false,
}: ReportPreviewContentProps) {
    const footnotePlan = useReportFootnotes(data);
    const { totalPages, sectionPageMap, getPageSections, getPageFootnotes, isPaginationReady } =
        useReportPagination(sections, data, editingBlockId, footnotePlan, freezePagination);

    const prevTotalPagesRef = useRef(0);

    useEffect(() => {
        if (isPaginationReady && onTotalPagesChange && prevTotalPagesRef.current !== totalPages) {
            prevTotalPagesRef.current = totalPages;
            onTotalPagesChange(totalPages);
        }
    }, [isPaginationReady, totalPages, onTotalPagesChange]);

    const prevSectionPageMapRef = useRef<Record<string, number>>({});

    useEffect(() => {
        if (isPaginationReady && onSectionPageMapChange) {
            const isDifferent =
                JSON.stringify(prevSectionPageMapRef.current) !== JSON.stringify(sectionPageMap);

            if (isDifferent) {
                prevSectionPageMapRef.current = sectionPageMap;
                onSectionPageMapChange(sectionPageMap);
            }
        }
    }, [isPaginationReady, onSectionPageMapChange, sectionPageMap]);

    const pagesToRender = showOnlyFirstPage
        ? [1]
        : Array.from({ length: totalPages }, (_, i) => i + 1);

    const headerCustomizations = data.customizations?.header;
    const letterheadUrl = resolvePreviewUrl(headerCustomizations?.letterhead_url);
    const showLetterhead = headerCustomizations?.show_letterhead && !!letterheadUrl;

    const { clipboardState, pasteAt, insertBlockAt, insertBlocksAt, getBlockClipboardFingerprint } =
        useTestBlockSelection();
    const { handleAddCustomSection, handleSectionClick, sidebarSteps } = useReportWizard();
    const { showInstrumentPaste } = usePastePreview();
    const customSectionsAccess = useFeatureEntitlement('reports.customSections');
    const canInsertImage = useFeatureFlag('reportImages.blocks').enabled;

    // The context-menu paste resolves asynchronously; it must not read a
    // render-time value that has since moved on.
    const clipboardStateRef = useRef(clipboardState);
    clipboardStateRef.current = clipboardState;

    // Resolved before the menu opens: Radix renders the content *at* the
    // pointer, so by the time an item's onSelect runs the topmost element there
    // is the menu itself, not the block that was clicked.
    const pendingInsertTargetRef = useRef<InsertTarget>('end');

    const handlePageContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        const blockEl = (event.target as HTMLElement).closest<HTMLElement>(
            '[data-preview-block-id]',
        );
        const attr = blockEl?.getAttribute('data-preview-block-id');
        pendingInsertTargetRef.current = attr ? { afterRealId: attrToRealId(attr) } : 'end';
    }, []);

    /** Only a file drag is claimed; block dragging is mouse-driven and unaffected. */
    const isFileDrag = (event: React.DragEvent) =>
        Array.from(event.dataTransfer?.types ?? []).includes('Files');

    const handleImageDragOver = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            if (!canInsertImage || disabled || !isFileDrag(event)) return;
            // Without this the browser opens the file instead of dropping it.
            event.preventDefault();
        },
        [canInsertImage, disabled],
    );

    /** Shared by the file drop and the clipboard image route. */
    const insertImage = useCallback(
        (file: File, target: InsertTarget) => {
            if (!canInsertImage) return;

            const validationError = validateImageFile(file);
            if (validationError) {
                toast.error(validationError);
                return;
            }

            const blockId = insertBlockAt('tool_image', target);
            if (blockId) setPendingImageFile(blockId, file);
        },
        [canInsertImage, insertBlockAt],
    );

    const handleImageDrop = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            if (!canInsertImage || disabled || !isFileDrag(event)) return;
            event.preventDefault();

            const file = event.dataTransfer.files?.[0];
            if (!file) return;

            // Same positioning rule as the context menu: the block under the
            // pointer decides where the new one lands.
            const blockEl = (event.target as HTMLElement).closest<HTMLElement>(
                '[data-preview-block-id]',
            );
            const attr = blockEl?.getAttribute('data-preview-block-id');
            const target: InsertTarget = attr ? { afterRealId: attrToRealId(attr) } : 'end';

            insertImage(file, target);
        },
        [canInsertImage, disabled, insertImage],
    );

    /**
     * Turns a classified clipboard route into blocks. Takes its target
     * explicitly because both callers resolve it before anything async runs.
     */
    const applyPasteRoute = useCallback(
        (route: PasteRoute, target: InsertTarget) => {
            switch (route.kind) {
                case 'blocks':
                    insertBlocksAt(route.payload.blocks, target);
                    return;
                case 'table':
                    // Same dialog the "Colar uma tabela pronta" menu item opens,
                    // already showing the grid: a table pasted on the laudo is a
                    // new instrument, and it gets named and cited like one.
                    showInstrumentPaste(
                        route.grid,
                        route.source,
                        (grid, identity) => {
                            insertBlocksAt(instrumentBlocksFromGrid(grid, identity), target);
                        },
                        { patientId: data.patient?.id },
                    );
                    return;
                case 'image':
                    insertImage(route.file, target);
                    return;
                case 'text':
                    insertBlocksAt([textBlockFromHtml(route.html)], target);
                    return;
                case 'none':
                    return;
            }
        },
        [data.patient?.id, insertBlocksAt, insertImage, showInstrumentPaste],
    );

    // Keyboard paste keeps landing at the end of the tests section, as it
    // always has; only the context menu carries a clicked position.
    const handleKeyboardPaste = useCallback(
        (route: PasteRoute) => applyPasteRoute(route, 'end'),
        [applyPasteRoute],
    );

    useReportPaste({ disabled: !!disabled, canInsertImage, onRoute: handleKeyboardPaste });

    const handleContextMenuPaste = useCallback(() => {
        // Snapshot before anything async: the next right-click overwrites the ref.
        const target = pendingInsertTargetRef.current;

        // Deliberately not awaited before the read — Safari ties clipboard.read()
        // to the user activation that started this task.
        void readSystemClipboard().then((snapshot) => {
            const carried = clipboardStateRef.current;

            // Clipboard unreadable (permission denied, or the API is missing):
            // the carried blocks are the only thing left to act on.
            if (!snapshot) {
                if (carried) {
                    pasteAt(target);
                    return;
                }
                toast.error(
                    'Não foi possível ler a área de transferência. Tente colar com o teclado.',
                );
                return;
            }

            // Same rule as resolvePastePayload on the keyboard path: the carried
            // blocks are trusted only while the system clipboard still holds the
            // fingerprint written for them. Copying in another app cannot clear
            // clipboardState, so without this a stale block would win over what
            // the user actually copied.
            if (carried && snapshot.text === getBlockClipboardFingerprint()) {
                pasteAt(target);
                return;
            }

            const route = classifyPaste(snapshot, { canInsertImage });
            if (route.kind === 'none') {
                toast.info('Não há nada para colar.');
                return;
            }

            applyPasteRoute(route, target);
        });
    }, [applyPasteRoute, canInsertImage, getBlockClipboardFingerprint, pasteAt]);

    const handleInsert = useCallback(
        (toolId: string) => {
            // A chart is never created directly: the config modal collects a
            // source table and drops the chart next to it, which beats any
            // position derived from the click.
            if (toolId === 'tool_chart') {
                onAddBlock(toolId);
                return;
            }
            // Instruments go through the selector modal; capture the click
            // target now — the ref may move before the modal confirms.
            if (toolId === 'tool_test') {
                const target = pendingInsertTargetRef.current;
                capture('report_instrument_add_clicked', { surface: 'context_menu' });
                if (onAddInstrument) {
                    onAddInstrument(
                        (blocks) => insertBlocksAt(blocks, target),
                        typeof target === 'object' ? target.afterRealId : null,
                    );
                } else {
                    onAddTest();
                }
                return;
            }
            insertBlockAt(toolId, pendingInsertTargetRef.current);
        },
        [insertBlockAt, insertBlocksAt, onAddBlock, onAddInstrument, onAddTest],
    );

    const navigableSections = sidebarSteps.map((step) => ({ id: step.id, title: step.title }));

    // Named `via` so the canvas actually moves: the bare handler defaults to
    // 'preview', which deliberately syncs the sidebars without scrolling.
    const handleGoToSection = (sectionId: string) => handleSectionClick(sectionId, 'page_menu');

    const isReadOnly = isCompletedStatus(data.status);

    if (isLoading) return <ReportPreviewSkeleton />;

    return (
        <div
            className="preview-ui-font relative mx-auto flex flex-col gap-8"
            data-report-pagination-ready={isPaginationReady ? 'true' : 'false'}
            style={{
                fontFamily: data.customizations?.font?.family || DEFAULT_FONT,
                width: A4_PAGE_WIDTH,
            }}
        >
            {isReadOnly && <ReadOnlyBanner />}
            <HiddenReportMeasurer
                data={data}
                disabled={Boolean(disabled)}
                psychologist={user}
                footnotePlan={footnotePlan}
            />

            {!isPaginationReady && <ReportPreviewSkeleton />}

            {isPaginationReady &&
                pagesToRender.map((page) => {
                    const pageSections = getPageSections(page) as RenderableSection[];
                    const pageFootnotes = getPageFootnotes(page);
                    const isLastPage = page === totalPages;

                    const isEditingOnThisPage = pageSections.some((section) => {
                        if (editingBlockId && section.id === 'tests' && section.blocksOverride) {
                            // Pagination un-splits the block being edited, so the
                            // rendered id and editingBlockId can disagree on the
                            // split suffix; compare the underlying block ids.
                            const editingKey = stripSplitBlockId(editingBlockId);
                            return section.blocksOverride.some(
                                (b) => stripSplitBlockId(b.id || b.order) === editingKey,
                            );
                        }
                        return false;
                    });

                    return (
                        <PageContextMenu
                            key={page}
                            disabled={!!disabled}
                            hasBlockClipboard={clipboardState !== null}
                            isCustomSectionBlocked={customSectionsAccess.shouldBlock}
                            customSectionPlan={customSectionsAccess.availableFromPlan}
                            sections={navigableSections}
                            canInsertImage={canInsertImage}
                            onInsert={handleInsert}
                            onPaste={handleContextMenuPaste}
                            onAddCustomSection={handleAddCustomSection}
                            onGoToSection={handleGoToSection}
                        >
                            <div
                                id={`report-page-${page}`}
                                className="report-paper flex flex-col overflow-visible bg-white shadow-xl"
                                onContextMenu={handlePageContextMenu}
                                onDragOver={handleImageDragOver}
                                onDrop={handleImageDrop}
                                style={{
                                    height: A4_PAGE_HEIGHT,
                                    ...(showLetterhead
                                        ? {
                                              backgroundImage: `url(${letterheadUrl})`,
                                              backgroundSize: '100% 100%',
                                              backgroundPosition: 'center',
                                              backgroundRepeat: 'no-repeat',
                                          }
                                        : {}),
                                }}
                            >
                                <div
                                    data-page-content
                                    className={cn(
                                        'flex-1 p-12',
                                        isEditingOnThisPage
                                            ? 'z-10 overflow-visible'
                                            : 'overflow-hidden',
                                    )}
                                >
                                    <ReportHeader
                                        data={data}
                                        psychologist={user}
                                        // Page 1 only: the header repeats on
                                        // every page, and so would the chrome.
                                        interactiveLogo={page === 1 && !disabled}
                                    />

                                    {page === 1 && (
                                        <PatientInfoTable
                                            data={data}
                                            onUpdate={onUpdate}
                                            disabled={disabled}
                                            psychologist={user}
                                        />
                                    )}

                                    {pageSections.map((section) => (
                                        <ReportSection
                                            key={section.key}
                                            section={section}
                                            data={data}
                                            onUpdate={onUpdate}
                                            disabled={disabled}
                                            onSectionClick={onSectionClick}
                                            isEditingSection={
                                                editingSection?.renderKey === section.key
                                            }
                                            isSectionEditing={
                                                !!editingSection &&
                                                editingSection.sectionId === section.id
                                            }
                                            onEditingSectionChange={onEditingSectionChange}
                                            renderKey={section.key}
                                            onAddTest={onAddTest}
                                            onAddBlock={onAddBlock}
                                            onAddChartFromTable={onAddChartFromTable}
                                            onEditChart={onEditChart}
                                            handleDeleteRequest={handleDeleteRequest}
                                            contentOverride={section.contentOverride}
                                            blocksOverride={section.blocksOverride}
                                            blockStartIndex={section.blockStartIndex}
                                            editingBlockId={editingBlockId}
                                            onEditingBlockChange={setEditingBlockId}
                                            droppableKey={section.key}
                                            commentCount={commentCountsBySection?.[section.id] ?? 0}
                                            onOpenAllComments={onOpenAllComments}
                                            footnotePlan={footnotePlan}
                                        />
                                    ))}

                                    {isLastPage && sections.length > 0 && (
                                        <PreviewPsychologistSignature
                                            full_name={user?.full_name}
                                            crp={user?.crp}
                                            city={user?.city}
                                            state={user?.state}
                                        />
                                    )}
                                </div>

                                <ReportPageFootnotes footnotes={pageFootnotes} />

                                <ReportFooter
                                    currentPage={page}
                                    totalPages={totalPages}
                                    data={data}
                                    psychologist={user}
                                />
                            </div>
                        </PageContextMenu>
                    );
                })}
        </div>
    );
});

const ReportPreview = memo(ReportPreviewComponent, (prevProps, nextProps) => {
    return (
        prevProps.data === nextProps.data &&
        prevProps.onUpdate === nextProps.onUpdate &&
        prevProps.onTotalPagesChange === nextProps.onTotalPagesChange &&
        prevProps.disabled === nextProps.disabled &&
        prevProps.onAddTest === nextProps.onAddTest &&
        prevProps.onAddChartFromTable === nextProps.onAddChartFromTable &&
        prevProps.onEditChart === nextProps.onEditChart &&
        prevProps.onAddBlock === nextProps.onAddBlock &&
        prevProps.onAddInstrument === nextProps.onAddInstrument &&
        prevProps.freezePagination === nextProps.freezePagination &&
        prevProps.onSectionClick === nextProps.onSectionClick &&
        prevProps.onSectionPageMapChange === nextProps.onSectionPageMapChange &&
        prevProps.showOnlyFirstPage === nextProps.showOnlyFirstPage &&
        prevProps.isLoading === nextProps.isLoading &&
        prevProps.commentCountsBySection === nextProps.commentCountsBySection &&
        prevProps.onOpenAllComments === nextProps.onOpenAllComments
    );
});

export default ReportPreview;
